import { Check, ChevronDown, Search, X } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Searchable single-select.
 *
 * Same component as the driver PWA's `SelectSearch`, restyled
 * for desktop (smaller spacing, lighter borders). Used in the
 * planning dialog and filters for the driver / vehicle / client
 * pickers — the API can return up to 100 entries, which is too
 * long for a native `<select>` and too short to justify a fully
 * virtualised library.
 */

export interface SelectSearchOption {
  value: string
  label: string
  /** Secondary text shown on a second line. */
  detail?: string
}

type SearchableField = keyof Pick<SelectSearchOption, 'label' | 'detail'>

const DEFAULT_SEARCHABLE_FIELDS: ReadonlyArray<SearchableField> = ['label', 'detail']

interface SelectSearchProps {
  placeholder?: string
  value: string | null
  onChange: (value: string | null) => void
  options: readonly SelectSearchOption[]
  searchableFields?: ReadonlyArray<SearchableField>
  loading?: boolean
  disabled?: boolean
  invalid?: boolean
  /** Show the "Aucun" clear-row — defaults to true. */
  allowClear?: boolean
  /** Optional className applied to the trigger button. */
  className?: string
  /** Forwarded to the underlying button (id, aria-*, …). */
  triggerId?: string
}

export function SelectSearch({
  placeholder = 'Sélectionner…',
  value,
  onChange,
  options,
  searchableFields = DEFAULT_SEARCHABLE_FIELDS,
  loading = false,
  disabled = false,
  invalid = false,
  allowClear = true,
  className,
  triggerId,
}: SelectSearchProps) {
  const generatedId = useId()
  const id = triggerId ?? generatedId
  const panelId = `${id}-panel`
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selected = useMemo(
    () => options.find((opt) => opt.value === value) ?? null,
    [options, value],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return options
    return options.filter((opt) =>
      searchableFields.some((field) => {
        const v = opt[field]
        return typeof v === 'string' && v.toLowerCase().includes(q)
      }),
    )
  }, [options, query, searchableFields])

  // Close on outside click / Escape — only while the panel is open.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (open) searchInputRef.current?.focus()
    else setQuery('')
  }, [open])

  function handleSelect(next: string | null): void {
    onChange(next)
    setOpen(false)
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'h-10 w-full rounded-md border bg-surface px-3 text-sm text-left',
          'flex items-center justify-between gap-2',
          'transition-colors duration-100',
          'focus:outline-none',
          invalid
            ? 'border-danger'
            : open
              ? 'border-brand ring-2 ring-brand'
              : 'border-surface-border hover:border-text-muted',
          (disabled || loading) && 'opacity-60 cursor-not-allowed',
          className,
        )}
      >
        <span
          className={cn(
            'truncate',
            selected !== null ? 'text-text' : 'text-text-muted',
          )}
        >
          {loading
            ? 'Chargement…'
            : selected !== null
              ? selected.label
              : placeholder}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            'shrink-0 text-text-muted transition-transform duration-150',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          id={panelId}
          role="listbox"
          className={cn(
            // Float over the page rather than push content like the
            // driver PWA does — desktop has the room for it and
            // dropdowns reading down feels natural with a pointer.
            'absolute left-0 right-0 mt-1 z-30',
            'rounded-md border border-surface-border bg-surface shadow-lg',
            'overflow-hidden',
          )}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-border">
            <Search size={14} className="text-text-muted shrink-0" aria-hidden="true" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-muted"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query !== '' ? (
              <button
                type="button"
                aria-label="Effacer la recherche"
                onClick={() => setQuery('')}
                className="text-text-muted hover:text-text"
              >
                <X size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>

          <ul className="max-h-64 overflow-y-auto overscroll-contain">
            {allowClear ? (
              <li>
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm',
                    'flex items-center text-text-muted italic',
                    'hover:bg-surface-hover',
                    'border-b border-surface-border',
                  )}
                >
                  Aucun
                </button>
              </li>
            ) : null}
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-sm text-text-muted text-center">
                Aucun résultat
              </li>
            ) : (
              filtered.map((opt) => {
                const isActive = opt.value === value
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSelect(opt.value)}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm',
                        'flex items-center justify-between gap-2',
                        'hover:bg-surface-hover',
                        isActive && 'bg-info-soft text-brand font-medium',
                      )}
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="truncate">{opt.label}</span>
                        {opt.detail !== undefined && opt.detail !== '' ? (
                          <span className="text-xs text-text-muted truncate">
                            {opt.detail}
                          </span>
                        ) : null}
                      </span>
                      {isActive ? (
                        <Check size={14} className="text-brand shrink-0" aria-hidden="true" />
                      ) : null}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
